"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import { memo, useState, type FC } from "react";
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
  type CodeHeaderProps,
} from "@assistant-ui/react-markdown";
import { CheckIcon, CopyIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { prepareMarkdownForAsciiGrids } from "@/lib/prepare-markdown-mixed-tables";
import { remarkPreprocessBrokenGfmTables } from "@/lib/remark-preprocess-broken-gfm-tables";
import { cn } from "@/lib/utils";

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      /** Smooth typing animates character-by-character; incomplete GFM (e.g. pipe tables) then renders as raw text. Prefer correct markdown. */
      smooth={false}
      remarkPlugins={[remarkPreprocessBrokenGfmTables, remarkGfm, remarkBreaks]}
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

/** Renders a raw string as markdown (tables, lists, etc.). Use for text after chip or any fragment. */
export const MarkdownFromText: FC<{ text: string }> = memo(({ text }) => {
  if (!text.trim()) return null;
  const prepared = prepareMarkdownForAsciiGrids(text);
  return (
    <div className="break-words leading-7 text-foreground [&>*:not([data-md-hr])]:my-1.5 [&>*]:first:mt-0 [&>*]:last:mb-0">
      <ReactMarkdown
        remarkPlugins={[
          remarkPreprocessBrokenGfmTables,
          remarkGfm,
          remarkBreaks,
        ]}
        components={defaultComponents}
      >
        {prepared}
      </ReactMarkdown>
    </div>
  );
});
MarkdownFromText.displayName = "MarkdownFromText";

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-t-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white">
      <span className="lowercase [&>span]:text-xs">{language}</span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, children, ...props }) => (
    <h1
      className={cn(
        "scroll-m-20 text-2xl font-bold tracking-tight last:mb-0",
        className,
      )}
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ className, children, ...props }) => (
    <h2
      className={cn(
        "mt-2 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ className, children, ...props }) => (
    <h3
      className={cn(
        "mt-2 scroll-m-20 text-lg font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ className, children, ...props }) => (
    <h4
      className={cn(
        "mt-2 scroll-m-20 text-base font-semibold tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    >
      {children}
    </h4>
  ),
  h5: ({ className, children, ...props }) => (
    <h5
      className={cn("text-sm font-semibold first:mt-0 last:mb-0", className)}
      {...props}
    >
      {children}
    </h5>
  ),
  h6: ({ className, children, ...props }) => (
    <h6
      className={cn("text-xs font-semibold first:mt-0 last:mb-0", className)}
      {...props}
    >
      {children}
    </h6>
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "my-1.5 break-words text-xs leading-5 first:mt-0",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn(
        "font-medium text-primary underline underline-offset-4",
        className,
      )}
      {...props}
    >
      {props.children}
    </a>
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("border-l-2 pl-6 italic", className)}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "my-1 ml-6 list-disc text-xs leading-3 [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "my-1 ml-6 list-decimal text-xs leading-4 [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  // Padding (not margin) so spacing survives parent `[&>*]:my-*` and margin collapse.
  hr: ({ className, ...props }) => (
    <div className="py-8" data-md-hr="" role="presentation">
      <hr
        className={cn("m-0 border-0 border-t border-border", className)}
        {...props}
      />
    </div>
  ),
  table: ({ className, ...props }) => (
    <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden leading-3">
      <table
        className={cn(
          "my-5 w-full min-w-max border-separate border-spacing-0 text-xs",
          className,
        )}
        {...props}
      >
        {props.children}
      </table>
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "border-foreground/30 bg-background px-2 py-1 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "border-b border-l border-foreground/20 px-2 py-1 text-left last:border-r [&[align=center]]:text-center [&[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "m-0 border-b border-foreground/20 bg-background/70 p-0 first:border-t odd:bg-background/40 [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("[&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "min-w-0 overflow-x-auto rounded-b-lg bg-muted/80 p-4 font-mono text-xs leading-normal text-foreground",
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock && "rounded border bg-background font-semibold",
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});
